// src/admin/components/Editor.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

let JoditEditor = null;
try {
  JoditEditor = require("jodit-react").default;
} catch (_) { /* no-op */ }

export default function Editor({ value, onChange, minHeight = 260 }) {
  // Keep a local buffer so typing doesn’t continuously re-render parent
  const [local, setLocal] = useState(value || "");
  const lastPropValueRef = useRef(value || "");

  // If parent value changes externally (e.g., loading an item), sync once
  useEffect(() => {
    if (value !== lastPropValueRef.current) {
      lastPropValueRef.current = value || "";
      setLocal(value || "");
    }
  }, [value]);

  // Stable config so Jodit isn’t torn down/rebuilt every render
  const config = useMemo(
    () => ({
      readonly: false,
      minHeight,
      // These reduce layout jumps/jitter while typing
      toolbarAdaptive: false,
      toolbarSticky: false,
      spellcheck: true,
      // Optional: keep things simple while typing
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
    }),
    [minHeight]
  );

  const commitChange = useCallback(
    (html) => {
      // Update local and notify parent only on blur (or explicit commit),
      // avoiding a parent re-render on every keystroke.
      setLocal(html);
      lastPropValueRef.current = html;
      onChange && onChange(html);
    },
    [onChange]
  );

  if (JoditEditor) {
    return (
      <JoditEditor
        value={local}
        config={config}
        // Update only local buffer while typing to avoid jitter
        onChange={(html) => setLocal(html)}
        // Commit to parent when user leaves the editor (no shake)
        onBlur={(html) => commitChange(html)}
      />
    );
  }

  // Fallback textarea stays controlled by local buffer for same reason
  return (
    <textarea
      className="form-control"
      rows={8}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={(e) => commitChange(e.target.value)}
      style={{ minHeight }}
    />
  );
}
